import React from "react";
import EntryBase from "../../../components/EntryBase";
import EntryFiles from "../../../components/EntryFiles";
import EntryGridAr from "../../../components/EntryGridAr";
import EntryImages from "../../../components/EntryImages";
import EntryRemarks from "../../../components/EntryRemarks";
import WashGrid from "../../../components/WashGrid";

// BASE / IMAGE / WashGrid / 通常 Grid / FILE / 備考の切り替えを担当します。
export function createPanelContentRenderer(deps) {
    const {
        G_id,
        PANEL_TYPE,
        FUKUSHIZAI_BASE_PANEL_TYPE,
        fnc,
        controller,
        edittingcode,
        isEmpty,
        isEnabled,
        DicVal,
        textfields,
        settextfields,
        entrydataRef,
        EntrybodyRef,
        layoutlock,
        imageRef,
        fileRef,
        columninfo,
        rerender,
        ValueChanged,
        onFirstDataRendered,
        handleGridValueChanged,
        storeRowData,
        ensureGridDataRef,
        ensureGridColRef,
        buildEntryGridProps,
        buildWashGridFunctionRef,
        gridRenderKey,
        demandGridFixedHeight,
        sijisuGridFixedHeight,
        genkaGridFixedHeight,
        basicGridFixedHeight,
        isWashPanelInfo,
        isDemandPanelInfo,
        isColorwayPanel,
        handlePanelGridCellClicked,
        sustainableDisplayTextFields,
        isFukushizaiCopyPreviewing,
        fukushizaiPreviewEntryDataRef,
        buildFukushizaiPreviewEntryDataForBase,
        pendingFukushizaiCopy
    } = deps;

    // 原価 Grid かどうかを判定します。
    function isGenkaGridPanel(panel, forceGenkaPanel) {
        return isEnabled(forceGenkaPanel) || (!isEmpty(panel) && panel.grid && (
            String(panel.type) === PANEL_TYPE.GENKA ||
            String(panel.type) === "19" ||
            String(panel.id) === "19" ||
            String(panel.id) === "genka" ||
            String(panel.totaltype) === PANEL_TYPE.GENKA_SUMMARY
        ));
    }

    // 通常 Grid レイアウトで使う高さを決定します。
    function resolveFixedGridHeight(panel, fixedNormalHeight, isDemandPanel, isSijisuPanel, genkaPanel, basicGridPanel) {
        if (!isEmpty(fixedNormalHeight)) return fixedNormalHeight;
        if (isDemandPanel) return demandGridFixedHeight(panel);
        if (isSijisuPanel) return sijisuGridFixedHeight(panel);
        if (genkaPanel) return genkaGridFixedHeight(panel);
        if (basicGridPanel) return basicGridFixedHeight(panel);
        return "300px";
    }

    // Grid クリック時の列別補正をまとめます。
    function onGridCellClicked(panel, param) {
        if (isEmpty(param) || isEmpty(param.colDef) || isEmpty(param.node)) return;
        if (isColorwayPanel(panel)) {
            if (param.colDef.colId !== "expand") return;
            param.node.setExpanded(!param.node.expanded);
            return;
        }
        handlePanelGridCellClicked(panel, param);
    }

    // 個別 panel の本体を描画します。
    function renderPanelContents(props) {
        const isWashPanel = isWashPanelInfo(props.panel);
        const isDemandPanel = !isEmpty(props.panel) && props.panel.grid && isDemandPanelInfo(props.panel);
        const isSijisuPanel = !isEmpty(props.panel) && props.panel.grid && (String(props.panel.type) === PANEL_TYPE.SIJISU || String(props.panel.id) === "sijisu");
        const genkaPanel = isGenkaGridPanel(props.panel, props.forceGenkaPanel);
        const basicGridPanel = !isEmpty(props.panel)
            && props.panel.grid
            && !isWashPanel
            && !isDemandPanel
            && !isSijisuPanel
            && !isColorwayPanel(props.panel)
            && !genkaPanel;
        // 既存寄せ: 通常 GRID は行数連動。特殊系は既存個別表示を優先します。
        const forceNormalGridLayout = isEnabled(props.forceNormalLayout) || isSijisuPanel || basicGridPanel || genkaPanel;
        const fixedGridHeight = resolveFixedGridHeight(
            props.panel,
            props.fixedNormalHeight,
            isDemandPanel,
            isSijisuPanel,
            genkaPanel,
            basicGridPanel
        );

        if (props.panel.id === G_id.BASE) {
            const baseid = "basepanel" + props.panel.type;
            const entryBaseTextFields = String(props.panel.type ?? "") === PANEL_TYPE.SUSTAINABLE_MARK
                ? sustainableDisplayTextFields(textfields, props.panel.type)
                : textfields;
            const baseEntryDataRef = String(props.panel.type ?? "") === FUKUSHIZAI_BASE_PANEL_TYPE && isFukushizaiCopyPreviewing
                ? (() => {
                    fukushizaiPreviewEntryDataRef.current = buildFukushizaiPreviewEntryDataForBase(pendingFukushizaiCopy);
                    return fukushizaiPreviewEntryDataRef;
                })()
                : entrydataRef;

            return (
                <div id={baseid}>
                    <EntryBase
                        fnc={fnc}
                        textfields={entryBaseTextFields}
                        settextfields={settextfields}
                        panel={props.panel}
                        ValueChanged={ValueChanged}
                        entrydataRef={baseEntryDataRef}
                        EntrybodyRef={EntrybodyRef}
                        layoutlock={layoutlock || (String(props.panel.type ?? "") === "113" && isFukushizaiCopyPreviewing)}
                    />
                </div>
            );
        }

        if (props.panel.id === G_id.IMAGE) {
            return (
                <EntryImages
                    fnc={fnc}
                    controller={controller}
                    code={edittingcode}
                    dataRef={imageRef}
                    rerender={rerender}
                />
            );
        }

        if (isWashPanel) {
            return (
                <WashGrid
                    id={fnc.current.GridID(props.panel)}
                    table="d_inst_wash"
                    entrydataRef={entrydataRef}
                    dataRef={ensureGridDataRef(props.panel)}
                    columnRef={ensureGridColRef(props.panel)}
                    type={String(props.panel.type ?? "")}
                    fnc={buildWashGridFunctionRef(props.panel)}
                    onFirstDataRendered={onFirstDataRendered}
                    ValueChanged={(params) => handleGridValueChanged(props.panel, params)}
                    RowValueChanged={() => storeRowData(props.panel)}
                    RemoveRow={() => storeRowData(props.panel)}
                    panel={props.panel}
                />
            );
        }

        if (props.panel.grid) {
            const gridProps = buildEntryGridProps(props, {
                renderKey: gridRenderKey(props.panel),
                forceNormalLayout: forceNormalGridLayout,
                fixedNormalHeight: fixedGridHeight,
                isDemandPanel: isDemandPanel,
                hideRowNumbers: props.hideRowNumbers,
                masterDetail: props.masterDetail,
                detailCellRendererParams: props.detailCellRendererParams,
                rowDrag: props.rowDrag,
                onGridCellClicked: (param) => onGridCellClicked(props.panel, param),
                onGridCellDoubleClicked: undefined,
                getRowStyle: props.getRowStyle,
                QuerySetColumn: props.QuerySetColumn,
                withEntryBodyRef: props.panel.id !== G_id.PL
            });
            return <EntryGridAr {...gridProps} />;
        }

        if (props.panel.id === G_id.FILE) {
            return (
                <EntryFiles
                    fnc={fnc}
                    controller={controller}
                    code={edittingcode}
                    dataRef={fileRef}
                    rerender={rerender}
                />
            );
        }

        if (props.panel.id === G_id.NB) {
            if (isEmpty(columninfo)) return null;
            const coldef = columninfo.find((e) => e.type === props.panel.type);
            return (
                <EntryRemarks
                    id={"remarkspanel" + props.panel.type}
                    def={coldef}
                    dataRef={entrydataRef}
                    fnc={fnc}
                    ValueChanged={ValueChanged}
                />
            );
        }

        return null;
    }

    return {
        renderPanelContents
    };
}
